import { darken } from 'polished';
import React, { useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Flexer } from '../app/dashboard/elements/LayoutElements';
import { Colors } from './Colors';

export type MeldedInputProps = {
    placeholder?: string,
    value: string,
    autoFocus?: boolean,
    onChange: (value: string) => void,
    onCommit: () => void,
    children?: React.ReactNode,
};

const Frame = styled.div<{ active: boolean }>`
    padding: .5rem;
    border: solid 1px ${Colors.border};
    border-radius: 0.5rem;
    cursor: text;

    &:hover {
        border: solid 1px ${darken(0.1, Colors.border)};
    }

    ${p => p.active && css`
        border: solid 1px ${Colors.primary} !important;
        box-shadow: 0 0 0 1px ${Colors.primary};
    `}
`;

const TextArea = styled.textarea`
    border: none;
    outline: none;
    resize: none;
    width: 100%;
    height: 100%;
`;

const Base = ({ value, placeholder, autoFocus, onChange, onCommit, children }: MeldedInputProps) => {

    const [active, setActive] = useState(false);
    const textArea = useRef<HTMLTextAreaElement>(null);

    const onInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Support ctrl/shift for multiline
        const ignore = e.ctrlKey || e.shiftKey;
        if (e.key === 'Enter' && !ignore) {
            e.preventDefault();
            onCommit();
        }
    }, [onCommit]);

    return <Frame active={active} onClick={() => textArea.current?.focus()}>
        <TextArea
            ref={textArea}
            value={value}
            placeholder={placeholder}
            autoFocus={autoFocus}
            onChange={e => onChange(e.target.value)}
            onFocus={() => setActive(true)}
            onBlur={() => setActive(false)}
            onKeyDown={onInputKeyDown}
        />
        <Flexer flex="row" justify="space-between" align="center" gap="1">
            <Flexer flex="row" grow>
                {children}
            </Flexer>
        </Flexer>
    </Frame>;
};

const Actions = React.memo(({ left, right }: { left?: React.ReactNode, right?: React.ReactNode }) => {
    return <Flexer width="100%" flex="row" gap=".5" align="center" justify="space-between">
        <Flexer flex="row" gap=".5" align="center">
            {left}
        </Flexer>
        <Flexer grow flex="row" gap=".5" align="center" justify="flex-end">
            {right}
        </Flexer>
    </Flexer>;
});


export const MeldedInput = Object.assign(Base, {
    TextArea,
    Frame,
    Actions,
});
